/**
 * IXD Preview 비교 diff 테이블 1개를 toggle-ui-kit IxdTable 디자인으로 렌더한다.
 *
 * 정본(ixd-dsd-compare/table-cell.tsx)과 동일하게 헤더 셀은 IxdTable.Header,
 * 데이터 셀은 IxdTable.Cell 로 그린다. merge_info(rowspan/colspan)에 덮인 셀은
 * 백엔드 to_html 과 동일하게 건너뛴다(앵커 셀만 출력).
 *
 * period(당기/전기)는 table.id 의 접미사(`...:CURRENT`/`...:PREVIOUS`)에서 얻는다
 * (백엔드 _composed_ixd_report_factory: `{toc}::{idx}:{period.value}`).
 *
 * 선택 상태는 셀 단위로 구독한다(`selectedCellId === cellId`). 그래야 셀 클릭 시
 * 이전/새 선택 셀 2개만 리렌더되고, 테이블·다른 셀은 리렌더되지 않는다.
 */

import { memo } from "react";

import { IxdTable, type Period } from "@/components/ixd-table";
import { cn } from "@/lib/utils";
import type {
  PreviewDiffCell as PreviewDiffCellModel,
  PreviewDiffTable as PreviewDiffTableModel,
} from "@/apis/compare/route";
import { useIxdPreviewCompareStore } from "@/stores/ixd-preview-compare";
import { insertThousandSeparators } from "@/utils/ixd-editor/unit-conversion";

const periodOfTable = (tableId: string): Period =>
  tableId.split(":").pop() === "CURRENT" ? "당기" : "전기";

/**
 * 셀 포커스 링 클래스(단일 출처). inset box-shadow(ring)을 쓴다 — outline 은
 * border-collapse 테이블 셀에서, absolute overlay 는 td 가 containing block 을
 * 못 만들어 불안정하기 때문. 다운로드는 inline box-shadow 로 같은 시각을 낸다.
 * 다운로드 clone 정리(strip) 시에도 이 상수를 재사용한다.
 */
export const CELL_FOCUS_CLASS = "ring-2 ring-inset ring-indigo-500";

const PreviewDiffCell = memo(function PreviewDiffCell({
  cellId,
  pairCellId,
  cell,
  period,
  rowspan,
  colspan,
}: {
  cellId: string;
  /** 반대편(src↔trg) 매칭 셀 id. 본인/매칭 셀 중 하나라도 선택되면 함께 포커스. */
  pairCellId?: string;
  cell: PreviewDiffCellModel;
  period: Period;
  rowspan: number;
  colspan: number;
}) {
  const selected = useIxdPreviewCompareStore(
    (s) =>
      s.selectedCellId === cellId ||
      (pairCellId !== undefined && s.selectedCellId === pairCellId),
  );
  const setSelectedCellId = useIxdPreviewCompareStore(
    (s) => s.setSelectedCellId,
  );

  const shared = {
    rowSpan: rowspan > 1 ? rowspan : undefined,
    colSpan: colspan > 1 ? colspan : undefined,
    diffStatus: cell.status,
    "data-cell-id": cellId,
    "data-pair-cell-id": pairCellId,
    onClick: (event: React.MouseEvent) => {
      event.stopPropagation();
      setSelectedCellId(selected ? null : cellId);
    },
    className: cn(
      "min-w-[120px] max-w-[240px] cursor-pointer",
      cell.isGhost && "opacity-36",
      selected && CELL_FOCUS_CLASS,
    ),
  };

  if (cell.type === "header") {
    return (
      <IxdTable.Header period={period} showDebitCreditChip={false} {...shared}>
        <span className="whitespace-pre-wrap [overflow-wrap:anywhere]">
          {cell.text}
        </span>
      </IxdTable.Header>
    );
  }

  return (
    <IxdTable.Cell {...shared}>
      <div className="min-h-[1em] w-full min-w-0 [overflow-wrap:anywhere]">
        {insertThousandSeparators(cell.text)}
      </div>
    </IxdTable.Cell>
  );
});

export const PreviewDiffTable = ({
  table,
  pairTableId,
  side,
}: {
  table: PreviewDiffTableModel;
  pairTableId?: string;
  /** 좌(src)/우(trg) 구분. src/trg 테이블 id 가 동일할 수 있어 cell id 충돌을
   *  막는 prefix 로 쓴다. */
  side: "src" | "trg";
}) => {
  const period = periodOfTable(table.id);
  const pairSide = side === "src" ? "trg" : "src";
  const covered = new Set<string>();

  // border-collapse 셀은 outline/box-shadow/positioning 이 렌더되지 않아 포커스
  // ring 이 안 보인다. border-separate(+spacing 0)로 두면 셀이 정상 박스로 동작하고,
  // IxdTable 은 우/하(+첫 셀 좌/상)만 그려 테두리 모양은 동일하게 유지된다.
  return (
    <table
      className="border-separate border-spacing-0"
      data-table-id={table.id}
    >
      <tbody>
        {table.grid.map((row, r) => (
          <tr key={r}>
            {row.map((cell, c) => {
              if (covered.has(`${r}:${c}`)) return null;
              const rowspan = Math.max(1, cell.rowspan);
              const colspan = Math.max(1, cell.colspan);
              for (let dr = 0; dr < rowspan; dr++) {
                for (let dc = 0; dc < colspan; dc++) {
                  covered.add(`${r + dr}:${c + dc}`);
                }
              }
              return (
                <PreviewDiffCell
                  key={c}
                  cellId={`${side}:${table.id}:${r}:${c}`}
                  pairCellId={
                    pairTableId
                      ? `${pairSide}:${pairTableId}:${r}:${c}`
                      : undefined
                  }
                  cell={cell}
                  period={period}
                  rowspan={rowspan}
                  colspan={colspan}
                />
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
